import React, { useEffect, useState, useCallback } from 'react';
import { useCalendarApi } from '../hooks/useCalendarApi';
import { useCalendarStore } from '../store/calendarStore';

interface CalendarDataProviderProps {
  children: React.ReactNode;
}

export const CalendarDataProvider: React.FC<CalendarDataProviderProps> = ({ children }) => {
  console.log('🚨🚨🚨 [CalendarDataProvider] COMPONENT IS RUNNING - THIS SHOULD ALWAYS SHOW! 🚨🚨🚨');
  console.log('🏁 [CalendarDataProvider] Component mounting...');
  
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  
  // Get real API functions
  const {
    fetchCalendars,
    fetchEvents,
    loading: apiLoading
  } = useCalendarApi();
  
  // Get store actions
  const {
    setCalendars,
    setEvents,
    setLoading,
    setError,
    clearError,
    connectWebSocket,
    disconnectWebSocket,
  } = useCalendarStore();

  // Initialize calendar data on component mount - REAL API CALLS
  const initializeData = useCallback(async () => {
    if (isInitialized) return;
    
    console.log('🚀 [CalendarDataProvider] Initializing calendar data with REAL APIs...');
    setLoading(true);
    clearError();
    setInitError(null);
    
    try {
      // Load calendars from REAL API
      console.log('📅 [CalendarDataProvider] Loading calendars from API...');
      const calendarsData = await fetchCalendars();
      console.log('✅ [CalendarDataProvider] Real API calendars response:', calendarsData);
      console.log('✅ [CalendarDataProvider] Calendars loaded count:', calendarsData?.length || 0);
      
      if (calendarsData && Array.isArray(calendarsData)) {
        console.log('📋 [CalendarDataProvider] Calendar details:', calendarsData.map(cal => ({
          id: cal.id,
          name: cal.name,
          color: cal.color,
          ownerId: cal.ownerId,
          isDefault: cal.isDefault,
          isVisible: cal.isVisible,
        })));
        
        // 🔍 Debug what we're setting in the store
        console.log('🏪 [CalendarDataProvider] Setting calendars in store...');
        setCalendars(calendarsData);
        
        // 🔍 Verify what got set
        setTimeout(() => {
          console.log('🔍 [CalendarDataProvider] Checking store state after setCalendars...');
        }, 100);
      } else {
        console.warn('⚠️ [CalendarDataProvider] Invalid calendars data format:', calendarsData);
        setCalendars([]);
      }
      
      // Load events from REAL API
      console.log('📝 [CalendarDataProvider] Loading events from API...');
      const eventsData = await fetchEvents();
      console.log('✅ [CalendarDataProvider] Real API events response:', eventsData);
      console.log('✅ [CalendarDataProvider] Events loaded count:', eventsData?.length || 0);
      
      if (eventsData && Array.isArray(eventsData)) {
        console.log('📝 [CalendarDataProvider] Event details:', eventsData.slice(0, 3).map(evt => ({
          id: evt.id,
          title: evt.title,
          calendarId: evt.calendarId,
          start: evt.start,
          end: evt.end,
          allDay: evt.allDay,
        })));
        setEvents(eventsData);
      } else {
        console.warn('⚠️ [CalendarDataProvider] Invalid events data format:', eventsData);
        setEvents([]);
      }

      // Connect realtime socket after initial calendar/event state is available
      connectWebSocket();
      
      setIsInitialized(true);
      console.log('🎉 [CalendarDataProvider] Calendar data initialization complete with REAL APIs!');
      
    } catch (error) {
      console.error('💥 [CalendarDataProvider] Failed to initialize calendar data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load calendar data';
      setError(errorMessage);
      setInitError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [isInitialized, fetchCalendars, fetchEvents, connectWebSocket]);

  useEffect(() => {
    console.log('🔄 [CalendarDataProvider] useEffect triggered, calling initializeData...');
    initializeData();

    return () => {
      disconnectWebSocket();
    };
  }, [initializeData, disconnectWebSocket]);

  // Show loading state during initialization
  if (!isInitialized && apiLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-0">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lex-accent mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Loading Calendar
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Fetching your calendars and events from API...
          </p>
        </div>
      </div>
    );
  }

  // Show error state if initialization failed
  if (initError && !isInitialized) {
    return (
      <div className="flex items-center justify-center h-full min-h-0">
        <div className="text-center max-w-md mx-auto">
          <div className="bg-red-100 dark:bg-red-900/20 rounded-lg p-6 mb-4">
            <div className="text-red-600 dark:text-red-400 mb-2">
              <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Failed to Load Calendar
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {initError}
            </p>
            <button
              onClick={() => {
                setIsInitialized(false);
                setInitError(null);
              }}
              className="px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render children once data is loaded - must preserve height chain
  return (
    <div className="h-full min-h-0">
      {children}
    </div>
  );
}; 
